import { ThemeKey } from "@smartsheet/design-tokens";
import { useEffect, useState } from "react";
import { BODY_MARKER_ATTRIBUTE } from "../tokenInjector/ThemeMarkerAttribute";

const getThemeKey = (fallback: ThemeKey) => (document.body.getAttribute(BODY_MARKER_ATTRIBUTE) as ThemeKey) ?? fallback;

export interface Unsubscribe {
    (): void;
}

export interface UseThemeKeyOptions {
    /**
     * If the theme tokens aren't present in the DOM somehow, fall back to this theme.
     * Defaults to whatever @smartsheet/design-tokens defaults to.
     */
    fallbackTheme?: ThemeKey;

    /**
     * If false, the listener (and therefore any updating) will be turned off.
     */
    isDisabled?: boolean;
}

export interface UseThemeKeyResult {
    /**
     * The applied theme's key.
     */
    themeKey: ThemeKey;
}

/**
 * This hook returns the current theme's key or null if none is present.
 */
export const useThemeKey = ({
    fallbackTheme = "lodestar-light",
    isDisabled,
}: UseThemeKeyOptions = {}): UseThemeKeyResult => {
    const [themeKey, setThemeKey] = useState<ThemeKey>(() => getThemeKey(fallbackTheme));

    // Hooks up a MutationObserver that watches the body class. This means any change
    // to add/remove the class we care about will cause a re-render here.
    // This is good because many components could use this hook and we want them all
    // to pay attention.
    useEffect(() => {
        if (isDisabled) {
            return;
        }
        // Do a quick update in case it has changed while we were disabled.
        setThemeKey(getThemeKey(fallbackTheme));

        const obs = new MutationObserver(() => setThemeKey(getThemeKey(fallbackTheme)));
        obs.observe(document.body, {
            attributes: true,
            attributeFilter: [BODY_MARKER_ATTRIBUTE],
        });
        return () => {
            obs.disconnect();
        };
    }, [isDisabled, fallbackTheme]);

    return {
        themeKey,
    };
};
